import { libSlotsReducer, LibSlotsStoreState } from '@bcraft-frontend-lib/slots'
import { libWheelReducer, LibWheelStoreState } from '@bcraft-frontend-lib/wheel'
// import { ILibStore, libReducer } from '@bcraft-project/frontend-lib'
import { AnyAction, applyMiddleware, combineReducers, compose, createStore, Reducer, Store } from 'redux'
import { persistStore } from 'redux-persist'
import { Persistor } from 'redux-persist/es/types'
import thunk from 'redux-thunk'

import { BaseHttpService } from 'services/api/BaseHttpService'

import * as AltenarFeature from 'features/altenar'
import * as AuthFeature from 'features/auth'
import * as BetbyFeature from 'features/betby'
import * as BonusHistoryFeature from 'features/bonusHistory'
import * as BonusSystemFeature from 'features/bonusSystem'
import * as DigitainFeature from 'features/digitain'
import * as JackpotsFeature from 'features/jackpots'
import * as LocaleFeature from 'features/locale'
import * as NotifiesFeature from 'features/notifies'
import * as PaymentFeature from 'features/payment'
import * as ProfileFeature from 'features/profile'
import * as PromocodeFeature from 'features/promocode'
import * as PWAFeature from 'features/pwa'
import * as SettingsFeature from 'features/settings'
import * as SidePanelFeature from 'features/sidePanel'
import * as SliderFeature from 'features/slider'
import * as SoftionFeature from 'features/softion'
import * as SupportFormFeature from 'features/supportForm'
import * as UpgamingFeature from 'features/upgaming'
import * as UserSettingsFeature from 'features/userSettings'
import * as WheelGameFeature from 'features/wheelGame'

import { IExtra } from '../shared/types/redux'
import { makeUnauthorizedInterceptor } from './interceptors/unauthorizedInterceptor'

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: typeof compose
  }
}

export type IApplicationState = LibWheelStoreState & LibSlotsStoreState & {
  auth: AuthFeature.types.IAuthStatePersisted
  locale: LocaleFeature.types.ILocaleState
  userSettings: UserSettingsFeature.types.IUserSettingsStatePersisted
  sidePanel: SidePanelFeature.types.ISidePanelState
  settings: SettingsFeature.types.ISettingsState
  profile: ProfileFeature.types.IProfileState
  payment: PaymentFeature.types.IPaymentsState
  bonusHistory: BonusHistoryFeature.types.IBonusHistoryState
  wheelGame: WheelGameFeature.types.IWheelGameState
  digitain: DigitainFeature.types.IDigitainState
  altenar: AltenarFeature.types.IAltenarState
  bonusSystem: BonusSystemFeature.types.IBonusSystemState
  promocode: PromocodeFeature.types.IPromocodesState
  softion: SoftionFeature.types.ISoftionState
  notifies: NotifiesFeature.types.INotifiesStatePersisted
  pwa: PWAFeature.types.IPWAStatePersisted
  jackpots: JackpotsFeature.types.IJackpotsState
  supportForm: SupportFormFeature.types.ISupportFormState
  slider: SliderFeature.types.ISliderStatePersisted
  betby: BetbyFeature.types.IBetbyState
  upgaming: UpgamingFeature.types.IUpgamingState
}

type ConfigureStore = {
  store: Store<IApplicationState>
  persistor: Persistor
}

export function configureStore(extra: IExtra): ConfigureStore {
  const middleware = thunk.withExtraArgument<IExtra>(extra)

  const composeEnhancers: any =
    window.__REDUX_DEVTOOLS_EXTENSION__ && process.env.NODE_ENV !== 'production'
      ? window.__REDUX_DEVTOOLS_EXTENSION__()
      : (arg: any) => arg

  const reducer: Reducer<IApplicationState, AnyAction> = combineReducers({
    auth: AuthFeature.reducer,
    locale: LocaleFeature.reducer,
    userSettings: UserSettingsFeature.reducer,
    sidePanel: SidePanelFeature.reducer,
    settings: SettingsFeature.reducer,
    profile: ProfileFeature.reducer,
    payment: PaymentFeature.reducer,
    bonusHistory: BonusHistoryFeature.reducer,
    wheelGame: WheelGameFeature.reducer,
    digitain: DigitainFeature.reducer,
    altenar: AltenarFeature.reducer,
    bonusSystem: BonusSystemFeature.reducer,
    promocode: PromocodeFeature.reducer,
    jackpots: JackpotsFeature.reducer,
    softion: SoftionFeature.reducer,
    notifies: NotifiesFeature.reducer,
    pwa: PWAFeature.reducer,
    supportForm: SupportFormFeature.reducer,
    slider: SliderFeature.reducer,
    betby: BetbyFeature.reducer,
    upgaming: UpgamingFeature.reducer,
    lib: combineReducers({
      slots: libSlotsReducer(),
      wheel: libWheelReducer,
    }),
  })

  const store = createStore(reducer, compose(applyMiddleware(middleware), composeEnhancers))

  const persistor = persistStore(store)

  BaseHttpService.addErrorInterceptor(makeUnauthorizedInterceptor(store))

  BaseHttpService.addTokenInterceptor(store)

  return { store, persistor }
}
